// mantine
import { Loader, Table, TextInput } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
// components
import type { TRegistryItem } from '@newo/api/gateway';
import NoRecords from '@/components/NoRecords';
import VersionItem from '@/pages/VersionControl/components/Registries/components/VersionsList/components/VersionItem';
// utils
import {
    TVersionControlRegistryVersion,
    useRegistryItemVersionsInfiniteQuery
} from '@/services/bff/bff.queries';

interface VersionsListProps {
    registryIdn: string;
    registryItemIdn: string;
    isProjectsV2Enabled?: boolean;
    isPublishPending: boolean;
    onDelete: (item: TRegistryItem) => void;
    onPublish: (item: TVersionControlRegistryVersion) => void;
}

const DEBOUNCE_TIMEOUT = 500;
const PER_PAGE = 100;

const VersionsList = ({
    registryIdn,
    registryItemIdn,
    isProjectsV2Enabled = false,
    isPublishPending,
    onDelete,
    onPublish
}: VersionsListProps) => {
    const [searchTerm, setSearchTerm] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const { data, isSuccess, isLoading } = useRegistryItemVersionsInfiniteQuery(
        registryIdn,
        registryItemIdn,
        {
            query: searchTerm,
            page: 1,
            per: PER_PAGE
        },
        {
            isProjectsV2Enabled
        }
    );

    const versions: TVersionControlRegistryVersion[] =
        data?.pages.flatMap<TVersionControlRegistryVersion>(
            (page) => page.items
        ) ?? [];

    return (
        <div className="flex flex-col gap-4">
            {isProjectsV2Enabled ? undefined : (
                <TextInput
                    className="flex-1 max-w-sm"
                    label="Search"
                    placeholder="Search by idn"
                    size="xs"
                    onChange={(event) => {
                        setSearchTerm(event.target.value);
                    }}
                />
            )}
            <Table highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Idn</Table.Th>
                        <Table.Th>Version</Table.Th>
                        <Table.Th>Active Clients</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Created at</Table.Th>
                        <Table.Th>Published at</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody className="[&>tr:last-child]:border-b-0!">
                    {isLoading ? (
                        <Table.Tr>
                            <Table.Td colSpan={7}>
                                <div className="flex justify-center items-center py-20">
                                    <Loader size={24} />
                                </div>
                            </Table.Td>
                        </Table.Tr>
                    ) : undefined}
                    {isSuccess && versions.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={7}>
                                <NoRecords />
                            </Table.Td>
                        </Table.Tr>
                    ) : undefined}
                    {versions.map((item) => (
                        <VersionItem
                            isProjectsV2Enabled={isProjectsV2Enabled}
                            isPublishPending={isPublishPending}
                            item={item}
                            key={item.id}
                            onDelete={onDelete}
                            onPublish={onPublish}
                        />
                    ))}
                </Table.Tbody>
            </Table>
        </div>
    );
};

export default VersionsList;
